import { AfterViewInit, Component, ElementRef, HostListener, Input, OnInit, QueryList, effect, WritableSignal, ViewChild, ViewChildren} from '@angular/core'
import { Answer, Document, Feedback, Source } from '../../../../types'
import { CommonModule } from '@angular/common'
import { RatingDialogComponent } from '../rating-dialog/rating-dialog.component'
import { ClipboardService } from 'ngx-clipboard'
import { EndWithCharPipe } from '../../../../pipes/end-with-char.pipe'
import { ResearchService } from '../../../../services/research.service'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { StatisticsService } from '../../../../services/statistics.service'
import { trigger, transition, style, animate, query, stagger } from '@angular/animations'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'
import { MessagehandlerService } from '../../../../services/messagehandler.service'
import { ConfigService } from '../../../../services/config.service'
import { LanguageService } from '../../../../services/language.service'

@Component({
   selector: 'app-sources',
   standalone: true,
   imports: [CommonModule, RatingDialogComponent, EndWithCharPipe, TranslocoModule],
   templateUrl: './sources.component.html',
   styles: '',
   animations: [
      trigger('fadeIn', [
         transition('* => *', [
            query(':enter', [
               style({ opacity: 0 }),
               stagger(100, [
                  animate('0.8s', style({ opacity: 1 }))
               ])
            ], { optional: true })
         ])
      ])
   ]
})

export class SourcesComponent implements OnInit, AfterViewInit {

   @Input() selectedSource!: WritableSignal<Source>
   @Input() answer?: Answer
   @Input() question?: string

   @ViewChildren('sourceButton') sourceButtons!: QueryList<ElementRef>
   @ViewChild('selectedSourceEl', { static: false }) selectedSourceEl!: ElementRef
   @ViewChild('ratingdialog', { static: false }) ratingdialogEl!: ElementRef
   @ViewChildren('abstract') abstracts!: QueryList<ElementRef>

   authorEtAlLabel = this.translocoService.translate('sources.etal')
   relatedResultsLink: SafeUrl = '#'
   currentReaction: Feedback['reaction']
   sources!: Document[]
   improvedQuery?: string
   improvedQuerySearch?: string
   id?: string

   get isDebug() { return this.research.isDebug }

   constructor(private clipboard: ClipboardService,
      private research: ResearchService,
      private lang: LanguageService,
      private translocoService: TranslocoService,
      private stats: StatisticsService,
      private message: MessagehandlerService,
      private domSanitizer: DomSanitizer,
      private configService: ConfigService) {

      effect(() => {
         if(this.selectedSource().source != null) {
            // the abstracts etc might not be rendered yet, so we need to wait a bit
            // this should probably be fixed by introducing a template for the selected source card, or a different signal thingy
            setTimeout(() => {
               this.focusOnSelectedSource(this.selectedSource().index)
               for (const el of this.abstracts)
                  this.markTextTruncated(el.nativeElement)
            }, 0)
         }
      })
   }

   ngOnInit() {
      if (this.answer && this.answer.results) {
         const { results, improvedQuery, improvedQuerySearch, id } = this.answer
         this.sources = results
         this.improvedQuery = improvedQuery
         this.improvedQuerySearch = improvedQuerySearch
         this.id = id
      }
      const addLangParam = this.configService.product !== 'summon'
      this.relatedResultsLink = this.domSanitizer.bypassSecurityTrustUrl(
         (this.improvedQuerySearch ?? '#') +
         (addLangParam ? '&lang=' + this.translocoService.getActiveLang() : '')
      )
   }

   ngAfterViewInit() {
      for (const el of this.abstracts)
         this.markTextTruncated(el.nativeElement)
   }

   getLangCode(source: Document) {
      if (source.languages && source.languages.length > 0) {
         const languages = source.languages.map(l => l.toLowerCase())
         // if there is an 'english' the abstract is probably in english (from some basic testing)
         if (languages.includes('english')) return 'en'
         return this.lang.getLanguageCode(languages[0]) ?? 'en'
      }
      return 'en'
   }

   // this sets a flag on the source if the text is truncated by the line-clamp css
   // because if so then we can show a "read more" button
   markTextTruncated(element: HTMLElement) {
      const truncated = element.scrollHeight > element.clientHeight
      const id = element.attributes.getNamedItem('data-id')?.value
      if (id) {
         const source = this.sources.find((s) => s.id === id)
         if (source) {
            source.readmore = truncated
         }
      }
   }

   @HostListener('window:keyup', ['$event'])
   keyEvent(event: KeyboardEvent) {
      if (event.key === 'Escape') {
         const selectedSource = this.sources.find((s) => s.selected)
         if(selectedSource) {
            this.closeSourceView(selectedSource)
         }
      }
   }

   rateThisSource(source: Document, what: 'thumbs_up' | 'thumbs_down') {
      if (source.feedback) {
         this.currentReaction = source.feedback.reaction
         if (source.feedback.reaction === what) {
            // cancel/remove rating
            source.feedback.reaction = ''
            this.stats.sendEvent({ category: 'feedback-remove', action: what, label: 'sourceId', value: source.id, question: this.question })
            this.research.removeFeedback(source.feedback)
         }
         else {
            source.feedback.reaction = what
         }
         source.feedback = {...source.feedback}
      }
      else {
         console.warn('something odd is happening, source has no feedback')
      }
   }

   copyText() {
      const selectedSource = this.sources.find((s) => s.selected)
      if (selectedSource) {
         const abstractLabel = this.translocoService.translate('sources.selectedsource.abstract')
         const copyText = `${selectedSource.title}. ${selectedSource.authors?.join(', ') ?? ''}${selectedSource.description ? '\r\n' + abstractLabel + selectedSource.description : ''}\r\n$DOI: {selectedSource.doi}`
         this.clipboard.copy(copyText)
         this.stats.sendEvent({ category: 'source', action: 'copy', label: 'sourceId', value: selectedSource.id, question: this.question })
      }
   }

   // note! this can also be triggered by clicking the round number icons in the answer
   selectSource(source?: Document, fromAnswer = false) {
      if (!fromAnswer && source?.selected) {
         this.stats.sendEvent({ category: 'source', action: 'collapse-from-sourcecard', label: 'sourceId', value: source?.id, question: this.question })
         // clicking on the card again should close it
         this.closeSourceView(source)
      }
      else {
         this.sources.map(s => s.selected = false)
         if (source) {
            source.selected = true
            if (fromAnswer) {
               this.stats.sendEvent({ category: 'source', action: 'expand-from-annotation', label: 'sourceId', value: source?.id, question: this.question })
            }
            else {
               this.stats.sendEvent({ category: 'source', action: 'expand-from-sourcecard', label: 'sourceId', value: source.id, question: this.question })
            }
            this.selectedSource.set({source, index: this.sources.indexOf(source) + 1})
         }
      }
   }

   closeSourceView(source: Document) {
      this.sources.map(s => s.selected = false)

      const index = this.sources.indexOf(source)
      if (index !== -1) {
         this.sourceButtons.toArray()[index]?.nativeElement?.focus()
         this.selectedSource.set({source: null, index: -1})
      }
   }

   downloadPdf(source: Document) {
      this.stats.sendEvent({ category: 'source', action: 'pdf', label: 'sourceId', value: source.id, question: this.question })
   }

   fullTextOnline(source: Document) {
      this.stats.sendEvent({ category: 'source', action: 'fulltext', label: 'sourceId', value: source.id, question: this.question })
   }

   getDeeplink(source: Document) {
      this.stats.sendEvent({ category: 'source', action: 'deeplink', label: 'sourceId', value: source.id, question: this.question})
      this.message.emitAppEvent({ event: 'source', status: 'click', value: source.deepLink })
   }

   getQuickLookLink(source: Document) {
      this.stats.sendEvent({ category: 'source', action: 'quickLookLink', label: 'sourceId', value: source.id, question: this.question})
      this.message.emitAppEvent({ event: 'source', status: 'click', value: source.quickLookLink })
   }

   viewRelated() {
      this.stats.sendEvent({ category: 'source', action: 'search-library', label: 'answerId', value: this.answer?.id, question: this.question })
   }

   getSourceTitle(index: number) {
      let title = this.answer?.results?.[index]?.title
      if (title && title[title.length - 1] !== '.') {
         title = title  + '.' // add dot so the screenreader will pause
      }
      index = index + 1 // we're accessing an array which is 0-based ¯\_(ツ)_/¯
      if (title) {
         return this.translocoService.translate('answer.sourcetitle', {index, title})
      }
      return this.translocoService.translate('answer.sourcetitledefault', {index})
   }

   readMore($event: Event, source: Document) {
      const readMoreParent = ($event.target as HTMLElement).parentElement
      if (source.readmore) {
         setTimeout(() => {
            for (const abstract of this.abstracts) {
               abstract.nativeElement.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' })
               abstract.nativeElement.focus()
            }
         }, 0)
      }

      source.readmore = !source.readmore
      if (readMoreParent) {
         // focus the whole abstract now it is expanded
         // we should think of a way to only focus the new text, but not sure how to do that here
         readMoreParent.focus()
      }
      this.stats.sendEvent({ category: 'source', action: 'readmore', label: 'sourceId', value: source.id, question: this.question})
   }

   // this is a super silly thing - but since this uses a classname multiple times (md:order-4 and md:order-7)
   // angular deduplicates them - INCORRECTLY!
   // anyway this took me some time to figure out... now doing it like this ¯\_(ツ)_/¯
   getClassForSourceSelected(idx: number) {
      switch (idx) {
      case 0:
         return 'order-1 md:order-4'
      case 1:
         return 'order-2 md:order-4'
      case 2:
         return 'order-3  md:order-4'
      case 3:
         return 'order-4  md:order-7'
      case 4:
         return 'order-5  md:order-7'
      default:
         return ''
      }
   }

   focusOnSelectedSource(index: number) {
      index = index - 1
      const button = this.selectedSourceEl
      console.log('focusing on source', index, button)
      if (button) {
         button.nativeElement.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' })
         // PREVENT SCROLL - otherwise it jumps because the scrollIntoView is not instant - due to the smooth behavior
         button.nativeElement.focus({preventScroll: true})
      }
   }


   /**
    * Below added keys for the transloco extractor to find the keys
    * t(content_type.album, content_type.architectural_drawing, content_type.archival_material, content_type.archival_material_manuscripts, content_type.art, content_type.artifact, content_type.atlas, content_type.audio_recording, content_type.audio_tape, content_type.audio_visual, content_type.blueprints, content_type.book, content_type.book_chapter, content_type.book_review, content_type.case, content_type.catalog, content_type.cd_dvd, content_type.ceremonial_object, content_type.citation, content_type.clothing, content_type.compact_disc, content_type.computer_file, content_type.conference_proceeding, content_type.course_reading, content_type.data_set, content_type.database, content_type.digital_collection, content_type.dissertation, content_type.drawing, content_type.dvd, content_type.ebook, content_type.ejournal, content_type.electronic_resource, content_type.equipment, content_type.exam, content_type.film, content_type.film_script, content_type.filmstrip, content_type.finding_aid, content_type.furnishing, content_type.globe, content_type.government_document, content_type.graphic_arts, content_type.houseware, content_type.ignored, content_type.image, content_type.implements, content_type.interactive_media, content_type.journal, content_type.journal_article, content_type.kit, content_type.learning_object, content_type.library_holding, content_type.library_research_guide, content_type.magazine, content_type.magazine_article, content_type.manuscript, content_type.map, content_type.market_research, content_type.microfilm, content_type.microform, content_type.model, content_type.monograph, content_type.music_manuscript, content_type.music_recording, content_type.music_score, content_type.musical_instrument, content_type.newsletter, content_type.newspaper, content_type.newspaper_article, content_type.online_archival_material, content_type.online_audio_recording, content_type.online_dissertation, content_type.online_image, content_type.online_map, content_type.online_music_score, content_type.online_video_recording, content_type.painting, content_type.pamphlet, content_type.paper, content_type.patent, content_type.performance, content_type.personal_article, content_type.personal_narrative, content_type.photograph, content_type.play, content_type.poem, content_type.postcard, content_type.poster, content_type.presentation, content_type.publication, content_type.publication_article, content_type.questionnaire, content_type.realia, content_type.reference, content_type.report, content_type.research_guide, content_type.research_publication, content_type.serial, content_type.sheet_music, content_type.slide, content_type.special_collection, content_type.spoken_word_recording, content_type.standard, content_type.streaming_audio, content_type.streaming_video, content_type.student_thesis, content_type.technical_report, content_type.tool, content_type.trade_publication, content_type.trade_publication_article, content_type.transcript, content_type.unknown, content_type.video_recording, content_type.web_resource, content_type.yearbook)
   */
   // optional content type localization
   getContentTypeLocalization(contentType: string|undefined) {
      // snake case contentType
      if (!contentType) return ''
      contentType = contentType.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()
      const t = this.translocoService.translate('content_type.' + contentType)
      if( t === 'content_type.' + contentType) {
         return contentType
      }
      return t
   }

}
